import { useState } from "react";
import type { ProjectContributionSnapshot } from "@afar-research/contracts";
import { Button } from "../ui/button";

/** The publisher approves the entire immutable payload; summary text is never an editor. */
export function ProjectContributionSnapshotPreview(props: {
  readonly snapshot: ProjectContributionSnapshot;
  readonly publishing: boolean;
  readonly onPublish: () => Promise<void>;
}) {
  const [approved, setApproved] = useState(false);
  const { snapshot } = props;
  return (
    <section className="space-y-4 rounded-lg border p-4">
      <h3 className="font-medium">Public snapshot preview</h3>
      <p className="text-xs text-muted-foreground">
        Self-reported figures through {snapshot.cutoff}. Additions and prompts do not measure
        effort, productivity, originality or quality.
      </p>
      <dl className="grid grid-cols-2 gap-3 text-sm">
        <div>
          <dt>Human additions</dt>
          <dd>{snapshot.additions.human.toLocaleString()}</dd>
        </div>
        <div>
          <dt>Agent additions</dt>
          <dd>{snapshot.additions.agent.toLocaleString()}</dd>
        </div>
        <div>
          <dt>Unknown additions</dt>
          <dd>{snapshot.additions.unknown.toLocaleString()}</dd>
        </div>
        <div>
          <dt>Human prompts</dt>
          <dd>{snapshot.humanPrompts.toLocaleString()}</dd>
        </div>
      </dl>
      <p className="text-xs text-muted-foreground">
        Human prompts count as human direction, separately from code additions. The agent generated
        this summary from recorded human prompts.
      </p>
      {(
        [
          ["Ideas and intent", snapshot.summary.ideas],
          ["Decisions and tradeoffs", snapshot.summary.decisions],
          ["Guidance and corrections", snapshot.summary.guidance],
          ["Agent execution", snapshot.summary.execution],
        ] as const
      ).map(([title, text]) => (
        <div key={title}>
          <h4 className="text-sm font-medium">{title}</h4>
          <p className="whitespace-pre-wrap break-words text-sm">{text}</p>
        </div>
      ))}
      <details className="text-xs">
        <summary className="cursor-pointer">
          Exact public payload, including the daily timeline
        </summary>
        <pre className="mt-2 max-h-80 overflow-auto whitespace-pre-wrap break-words rounded bg-muted p-3">
          {JSON.stringify(snapshot, null, 2)}
        </pre>
      </details>
      <label className="flex items-start gap-2 text-sm">
        <input
          type="checkbox"
          checked={approved}
          onChange={(event) => setApproved(event.target.checked)}
          disabled={props.publishing}
          className="mt-1"
        />
        I approve this summary and the complete public snapshot.
      </label>
      <Button
        size="sm"
        disabled={!approved || props.publishing}
        onClick={() => {
          if (approved && !props.publishing) void props.onPublish();
        }}
      >
        {props.publishing ? "Publishing…" : "Approve and publish"}
      </Button>
    </section>
  );
}
